
import React, { useState } from 'react';
import { format } from 'date-fns';
import { CheckCircle, Copy as CopyIcon } from 'lucide-react';
import { LicenseKey } from '@/types/licenseKey';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface LicenseKeyTableProps {
  licenseKeys: LicenseKey[];
  onSuspendLicense: (id: string) => Promise<LicenseKey>;
  onReactivateLicense: (id: string) => Promise<LicenseKey>;
  onDelete?: (id: string) => Promise<void>; // Added optional onDelete
  onUpdate?: (id: string, data: Partial<LicenseKey>) => Promise<LicenseKey>; // Added optional onUpdate
}

const LicenseKeyTable: React.FC<LicenseKeyTableProps> = ({
  licenseKeys,
  onSuspendLicense,
  onReactivateLicense,
  onDelete,
  onUpdate
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  
  const copyLicenseKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b">
            <th className="text-left py-3 px-4 text-sm font-medium">License Key</th>
            <th className="text-left py-3 px-4 text-sm font-medium">Plan</th>
            <th className="text-left py-3 px-4 text-sm font-medium">Domain</th>
            <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
            <th className="text-left py-3 px-4 text-sm font-medium">Expires</th>
            <th className="text-left py-3 px-4 text-sm font-medium">Instances</th>
            <th className="text-left py-3 px-4 text-sm font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {licenseKeys.map((license) => (
            <tr key={license.id} className="border-b hover:bg-gray-50">
              <td className="py-3 px-4">
                <div className="flex items-center">
                  <div className="font-mono text-sm truncate max-w-[150px] mr-2">
                    {license.key}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => copyLicenseKey(license.key)}
                  >
                    {copiedKey === license.key ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <CopyIcon className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </td>
              <td className="py-3 px-4 capitalize">
                <Badge variant="default">
                  Enterprise
                </Badge>
              </td>
              <td className="py-3 px-4">
                {license.domain || <span className="text-gray-400">Not set</span>}
              </td>
              <td className="py-3 px-4">
                <Badge variant={license.status === 'active' ? 'success' : 'destructive'}>
                  {license.status}
                </Badge>
              </td>
              <td className="py-3 px-4">
                {format(new Date(license.expiresAt), 'MMM d, yyyy')}
              </td>
              <td className="py-3 px-4">
                {license.usedInstances} / {license.maxInstances}
              </td>
              <td className="py-3 px-4">
                <div className="flex space-x-2">
                  {license.status === 'active' ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onSuspendLicense(license.id)}
                    >
                      Suspend
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReactivateLicense(license.id)}
                    >
                      Reactivate
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(license.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {licenseKeys.length === 0 && (
            <tr>
              <td colSpan={7} className="py-6 text-center text-gray-500">
                No license keys found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LicenseKeyTable;
